import { useCallback, useContext, useState } from 'react';
import { Stagiaire } from '../types';
import axios, { AxiosError } from 'axios';
import { AntiSpamContext } from '../Context';

export default function StagiaireContactForm({ stagiaire }: { stagiaire: Stagiaire; }) {
    const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const apiUrl = import.meta.env.VITE_API_URL || 'https://localhost';
    const { antiSpamToken, setAntiSpamToken } = useContext(AntiSpamContext);

    const sendForm = useCallback(async () => {
        try {
            const response = await axios.post(`${apiUrl}/api/stagiaire/contact/${stagiaire.id}`, {
                ...formData
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'X-Anti-Spam-Token': antiSpamToken || ''
                }
            });
            const { success, message } = response.data;
            if (!success) {
                setError('Une erreur est survenue lors de l\'envoi du formulaire.');
            } else {
                setSuccessMessage(message || 'Votre demande a été envoyée avec succès !');
                setAntiSpamToken(response.data.antiSpamToken);
                setFormData({ name: '', email: '', phone: '' });
            }
        } catch (err) {
            const error = err as AxiosError<any>;
            setError(error.response?.data?.message || 'Une erreur est survenue lors de l\'envoi du formulaire.');
        }
    }, [apiUrl, formData, stagiaire.id]);

    const validations = {
        name: {
            callback: (value: string) => value.trim().length > 0,
            errorMessage: 'Veuillez entrer votre nom.'
        },
        email: {
            callback: (value: string) => /.+@.+\.[a-zA-Z]{2,}/.test(value),
            errorMessage: 'Le format ne semble pas correspondre à une addresse email valide.'
        },
        phone: {
            callback: (value: string) => /^\+?[0-9\s\-()]{7,}$/.test(value),
            errorMessage: 'Le format ne semble pas correspondre à un numéro de téléphone valide.'
        },
    };

    const handleFormInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = () => {
        setError(null);
        const keys = Object.keys(formData);
        for (let key of keys) {
            const field = key as keyof typeof formData;
            const validation = validations[field];
            if (validation && !validation.callback(formData[field])) {
                setError(validation.errorMessage);
                return;
            }
        }
        sendForm();
    };

    return (
        <div>
            <div className='flex flex-col gap-2 mb-2'>
                <label htmlFor="name">Votre nom :
                    <input className='textInput' onInput={handleFormInput} type="text" id="name" name="name" value={formData.name} required />
                </label>

                <label htmlFor="email">Votre email :
                    <input className='textInput' onInput={handleFormInput} type="email" id="email" name="email" value={formData.email} required />
                </label>

                <label htmlFor="phone">Votre numéro de téléphone :
                    <input className='textInput' onInput={handleFormInput} type="tel" id="phone" name="phone" value={formData.phone} required />
                </label>

                <button onClick={handleSubmit} id="sendBtn" type="submit">Envoyer</button>
            </div>
            <div id="sendStatus" className={error ? 'sendErr' : successMessage ? 'sendOk' : ''}>
                {error && <>
                    {error}
                </>}
                {successMessage && <>
                    {successMessage}
                </>}
            </div>
        </div>
    );
}